import { useEffect, useState } from 'react';
import {
  fetchSubscription,
  fetchQuota,
  cancelSubscription,
  type SubscriptionInfo,
  type QuotaInfo,
} from '../services/api';
import { useT } from '../i18n';
import type { NavView } from '../components/Layout';

interface BillingProps {
  // Issue #593: upgrade vai para /pricing — o usuario escolhe entre
  // Single/Team (e o Enterprise virou contact-only, sem checkout).
  onNavigate?: (view: NavView) => void;
}

export default function Billing({ onNavigate }: BillingProps) {
  const t = useT();
  const [sub, setSub] = useState<SubscriptionInfo | null>(null);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  function loadAll() {
    setLoading(true);
    Promise.all([fetchSubscription(), fetchQuota().catch(() => null)])
      .then(([s, q]) => {
        setSub(s);
        if (q) setQuota(q);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadAll();
  }, []);

  async function handleCancel() {
    if (!confirm(t('billing.cancelConfirm'))) return;
    setActionLoading(true);
    setError(null);
    try {
      await cancelSubscription();
      loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setActionLoading(false);
    }
  }

  if (loading) return <p className="text-brand-600">{t('app.loading')}</p>;
  if (error && !sub) return <p className="text-red-600">{error}</p>;

  const plan = sub?.plan ?? 'free';
  const status = sub?.status ?? 'active';
  const pendingInvoices = sub?.pending_invoices ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-brand-800">{t('billing.title')}</h1>

      {error && (
        <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      )}

      {/* Current plan card */}
      <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-brand-800">{t('billing.currentPlan')}</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm text-brand-500">{t('billing.planLabel')}</p>
            <p className="text-xl font-bold capitalize text-brand-900">{plan}</p>
          </div>
          <div>
            <p className="text-sm text-brand-500">{t('billing.statusLabel')}</p>
            <p className="text-xl font-bold text-brand-900">{t(`billing.status.${status}`)}</p>
          </div>
          {sub?.current_period_end && (
            <div>
              <p className="text-sm text-brand-500">{t('billing.renewsOn')}</p>
              <p className="text-brand-900">{new Date(sub.current_period_end).toLocaleDateString()}</p>
            </div>
          )}
          {sub?.payment_method && (
            <div>
              <p className="text-sm text-brand-500">{t('billing.paymentMethod')}</p>
              <p className="uppercase text-brand-900">{sub.payment_method}</p>
            </div>
          )}
        </div>
      </div>

      {/* Usage card */}
      {quota && (
        <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-brand-800">{t('billing.usage')}</h2>
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-brand-600">
              <span>
                {t('billing.used', { used: quota.used, limit: quota.limit === 0 ? t('billing.unlimited') : quota.limit })}
              </span>
              <span>
                {quota.remaining === -1
                  ? t('billing.unlimited')
                  : t('billing.remaining', { count: quota.remaining })}
              </span>
            </div>
            <div className="mt-2 h-3 overflow-hidden rounded-full bg-brand-100">
              {quota.limit > 0 && (
                <div
                  className="h-full rounded-full bg-brand-600"
                  style={{ width: `${Math.min(100, (quota.used / quota.limit) * 100)}%` }}
                />
              )}
            </div>
            <p className="mt-2 text-xs text-brand-500">
              {t('billing.resetsOn', { date: quota.reset_date })}
            </p>
          </div>
        </div>
      )}

      {/* Pending invoices */}
      {pendingInvoices.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-6">
          <h2 className="text-lg font-semibold text-amber-800">{t('billing.pendingPayment')}</h2>
          {pendingInvoices.map((inv) => (
            <div key={inv.invoice_id} className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">
                  {t('billing.invoiceAmount', { amount: inv.amount.toFixed(2) })}
                </p>
                <p className="text-xs text-amber-600">
                  {t('billing.dueDate', { date: inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '-' })}
                </p>
              </div>
              <span className="rounded bg-amber-200 px-2 py-1 text-xs font-semibold text-amber-800">
                {inv.status}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        {plan === 'free' && (
          // Issue #593: nao ha mais um unico plano pago ('pro') — o
          // usuario escolhe Single ou Team na pagina de planos.
          <button
            type="button"
            onClick={() => onNavigate?.('pricing')}
            className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
          >
            {t('billing.viewPlans')}
          </button>
        )}
        {plan !== 'free' && status !== 'cancelled' && (
          <button
            type="button"
            disabled={actionLoading}
            onClick={handleCancel}
            className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
          >
            {actionLoading ? t('billing.processing') : t('billing.cancel')}
          </button>
        )}
      </div>
    </div>
  );
}
